import { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { Eye, EyeOff } from 'lucide-react'
import { cn } from '@lib/utils/helpers'
import { isSandboxEnvironment } from '@config/env'
import { showSuccess } from '@/lib/utils/notifications'
import { ValidationModal } from '@/components/ui/ValidationModal'
import { Tooltip } from '@/components/ui/Tooltip'
import type { ApiError } from '@/lib/api/apiClient'
import { Logo } from '../components/Logo'
import { SandboxBanner } from '../components/SandboxBanner'
import {
  CLOSED_MESSAGE_MODAL,
  VALIDATION_MESSAGE_TITLE,
} from '../constants/passwordReset'
import { submitNewPassword, verifyResetToken } from '../services/passwordResetService'
import type { MessageModalState, VerifyStatus } from '../types/passwordReset'
import { strengthBarColors } from '../utils/passwordStrength'
import { PASSWORD_MISMATCH_MESSAGE } from '../constants/validationMessages'
import {
  resetPasswordFormSchema,
  type ResetPasswordFormValues,
} from '../types/schemas'

export function ResetPassword() {
  const { resetToken } = useParams<{ resetToken: string }>()
  const navigate = useNavigate()
  const [status, setStatus] = useState<VerifyStatus>('pending')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [messageModal, setMessageModal] = useState<MessageModalState>(CLOSED_MESSAGE_MODAL)

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isValid },
  } = useForm<ResetPasswordFormValues>({
    resolver: zodResolver(resetPasswordFormSchema),
    defaultValues: { new_password: '', confirm_password: '' },
    mode: 'onChange',
  })

  const newPassword = watch('new_password')
  const confirmPassword = watch('confirm_password')
  const passwordsMismatch =
    !!newPassword && !!confirmPassword && newPassword !== confirmPassword
  const formAlert =
    errors.new_password?.message ||
    errors.confirm_password?.message ||
    (passwordsMismatch ? PASSWORD_MISMATCH_MESSAGE : '')

  const strengthColors = useMemo(
    () => strengthBarColors(newPassword || ''),
    [newPassword],
  )

  useEffect(() => {
    if (!resetToken) {
      setStatus('invalid')
      navigate('/login', { replace: true })
      return
    }

    let cancelled = false
    setStatus('pending')
    verifyResetToken(resetToken)
      .then(() => {
        if (!cancelled) setStatus('valid')
      })
      .catch((error: ApiError) => {
        if (cancelled) return
        setStatus('invalid')
        setMessageModal({
          isOpen: true,
          title: VALIDATION_MESSAGE_TITLE,
          message: error.message || 'This reset link is invalid or has expired.',
        })
      })

    return () => {
      cancelled = true
    }
  }, [resetToken, navigate])

  const onSubmit = handleSubmit(async (values) => {
    if (!resetToken) return
    setIsSubmitting(true)
    try {
      const message = await submitNewPassword(resetToken, values.new_password)
      showSuccess(message || 'Password Change Successfully', 3000)
      navigate('/login', { replace: true })
    } catch (error) {
      const apiError = error as ApiError
      setMessageModal({
        isOpen: true,
        title: VALIDATION_MESSAGE_TITLE,
        message:
          apiError.message || 'Failed to reset password. Please try again.',
      })
    } finally {
      setIsSubmitting(false)
    }
  })

  const handleMessageModalClose = () => {
    const wasInvalidLink = status === 'invalid'
    setMessageModal(CLOSED_MESSAGE_MODAL)
    if (wasInvalidLink) {
      navigate('/login', { replace: true })
    }
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <SandboxBanner />

      <div
        className={`flex items-center justify-center px-3 py-4 pb-24 sm:p-4 sm:pb-24 ${
          isSandboxEnvironment()
            ? 'min-h-[calc(100vh-40px)]'
            : 'min-h-screen'
        }`}
      >
        <div className="w-full max-w-md rounded-xl bg-white px-5 py-7 shadow-xl sm:p-8 md:p-10">
          <Logo />

          {status === 'pending' ? (
            <div className="py-8 text-center">
              <p className="font-medium text-gray-700">Verifying reset link…</p>
              <p className="mt-1 text-sm text-gray-500">This will only take a moment.</p>
            </div>
          ) : status === 'valid' ? (
            <>
              {formAlert ? (
                <div
                  className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
                  role="alert"
                >
                  {formAlert}
                </div>
              ) : null}

              <h1 className="mb-5 text-center text-2xl font-bold text-teal-dark sm:mb-6 sm:text-3xl">
                Reset Password
              </h1>

              <form onSubmit={onSubmit} noValidate className="space-y-5 sm:space-y-6">
                <div>
                  <label
                    htmlFor="new_password"
                    className="mb-2 block text-sm font-medium text-gray-700"
                  >
                    New Password
                  </label>
                  <div className="relative">
                    <input
                      id="new_password"
                      type={showNewPassword ? 'text' : 'password'}
                      autoComplete="new-password"
                      {...register('new_password')}
                      disabled={isSubmitting}
                      className={cn(
                        'w-full rounded-md border px-4 py-3 pr-12 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-teal-500',
                        errors.new_password ? 'border-red-500' : 'border-gray-300',
                        isSubmitting && 'cursor-not-allowed opacity-50',
                      )}
                    />
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      <Tooltip
                        content={showNewPassword ? 'Hide password' : 'Show password'}
                        side="top"
                      >
                        <button
                          type="button"
                          onClick={() => setShowNewPassword((v) => !v)}
                          className="text-gray-500 hover:text-gray-700"
                          aria-label={showNewPassword ? 'Hide password' : 'Show password'}
                        >
                          {showNewPassword ? (
                            <Eye className="h-5 w-5" />
                          ) : (
                            <EyeOff className="h-5 w-5" />
                          )}
                        </button>
                      </Tooltip>
                    </div>
                  </div>
                  <div
                    id="strength"
                    className="mt-2 flex flex-wrap items-center gap-2 text-xs text-gray-600"
                    aria-live="polite"
                  >
                    <small>Password strength:</small>
                    <ul className="m-0 flex list-none items-center gap-px p-0">
                      {strengthColors.map((color, i) => (
                        <li
                          key={i}
                          className="inline-block h-[5px] w-5 rounded-sm"
                          style={{ backgroundColor: color }}
                        />
                      ))}
                    </ul>
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="confirm_password"
                    className="mb-2 block text-sm font-medium text-gray-700"
                  >
                    Confirm Password
                  </label>
                  <div className="relative">
                    <input
                      id="confirm_password"
                      type={showConfirmPassword ? 'text' : 'password'}
                      autoComplete="new-password"
                      {...register('confirm_password')}
                      disabled={isSubmitting}
                      className={cn(
                        'w-full rounded-md border px-4 py-3 pr-12 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-teal-500',
                        errors.confirm_password || passwordsMismatch
                          ? 'border-red-500'
                          : 'border-gray-300',
                        isSubmitting && 'cursor-not-allowed opacity-50',
                      )}
                    />
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      <Tooltip
                        content={
                          showConfirmPassword ? 'Hide password' : 'Show password'
                        }
                        side="top"
                      >
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword((v) => !v)}
                          className="text-gray-500 hover:text-gray-700"
                          aria-label={
                            showConfirmPassword ? 'Hide password' : 'Show password'
                          }
                        >
                          {showConfirmPassword ? (
                            <Eye className="h-5 w-5" />
                          ) : (
                            <EyeOff className="h-5 w-5" />
                          )}
                        </button>
                      </Tooltip>
                    </div>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting || !isValid || passwordsMismatch}
                  className="w-full rounded-md bg-teal-dark px-6 py-3 font-bold text-white transition-colors hover:bg-teal-darker disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {isSubmitting ? 'Saving...' : 'Save'}
                </button>
              </form>

              <div className="mt-5 text-center sm:mt-6">
                <Link
                  to="/login"
                  className="text-sm font-medium text-teal-dark hover:text-teal-darker sm:text-base"
                >
                  ← Back to Login
                </Link>
              </div>
            </>
          ) : (
            <div className="py-6 text-center text-sm text-gray-600">
              Unable to verify this reset link.
            </div>
          )}
        </div>
      </div>

      <ValidationModal
        isOpen={messageModal.isOpen}
        title={messageModal.title}
        message={messageModal.message}
        confirmText="Ok"
        showValidationIcon={false}
        alignMessage
        okButtonStyle="saveChanges"
        onClose={handleMessageModalClose}
      />
    </div>
  )
}
